import { useCallback, useEffect, useState, type ReactElement } from "react";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { Route as RootRoute } from "../__root";
import { ConsentStepper } from "../../components/custom/ConsentStepper";
import { DiffViewer } from "../../components/custom/DiffViewer";
import { renderConsentMarkdown } from "../../lib/markdownRender";
import { apiCall } from "../../lib/apiClient";
import { hydrateAuth, useAuthStore } from "../../lib/authStore";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import type { ConsentAcceptPayload } from "../../schemas/consent";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/consent/re-prompt",
  component: () => <ConsentRePromptRoute />,
});

/**
 * /consent/re-prompt — material change flow (DEC-013 Policy C).
 *
 * Aktivuje se po loginu s ConsentRePromptRequired (temp token v `axigon_at`
 * cookie). Načte `latest_material_version` + text a vykreslí ConsentStepper s
 * diff view. Auth jede přes cookie (apiCall, credentials:"include").
 *
 * Po úspěšném accept backend vydá plnou session (access+refresh cookie) → tady
 * jen rehydratujeme auth store a navigujeme na dashboard dle role.
 */

interface RequiredResponse {
  requires_consent_re_prompt: boolean;
  latest_material_version: string | null;
  user_consent_text_version: string | null;
  body_markdown: string | null;
}

/** Vystředěný layout (sdílený vizuál s login stránkou). */
function ConsentShell({ children }: { children: ReactElement }): ReactElement {
  return (
    <main className="flex min-h-screen items-center justify-center bg-secondary p-4">
      <div className="w-full max-w-2xl">{children}</div>
    </main>
  );
}

export function ConsentRePromptRoute(): ReactElement {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState<string | null>(null);
  const [previousVersion, setPreviousVersion] = useState<string | null>(null);
  const [text, setText] = useState<string>("");
  const [previousText, setPreviousText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await apiCall<RequiredResponse>("/api/tenant/consent/required");
        if (cancelled) return;
        setVersion(data.latest_material_version);
        setPreviousVersion(data.user_consent_text_version);
        if (data.body_markdown && data.body_markdown.trim()) {
          setText(renderConsentMarkdown(data.body_markdown));
        } else {
          setText(buildLegacyFallback(data.latest_material_version ?? "current"));
        }
        // Tenant endpoint pro text libovolné starší verze zatím neexistuje (admin
        // /text je globalAdmin only) → diff fallbackne na placeholder.
        if (data.user_consent_text_version) {
          setPreviousText(buildPreviousVersionPlaceholder(data.user_consent_text_version));
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const handleLogout = useCallback(async () => {
    await apiCall("/api/public/auth/logout", { method: "POST" }).catch(() => {});
    useAuthStore.getState().reset();
    void navigate({ to: "/login", search: { next: undefined } });
  }, [navigate]);

  const handleAccept = useCallback(
    async (payload: ConsentAcceptPayload) => {
      // Backend na úspěch vydá plnou session (cookies). Necháme apiCall vyhodit
      // ApiError → ConsentStepper ho zobrazí ve své error oblasti.
      await apiCall("/api/tenant/consent/accept", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const state = await hydrateAuth();
      if (state.state === "authenticated" && state.user.role === "globalAdmin") {
        void navigate({ to: "/admin/dashboard" });
      } else {
        void navigate({ to: "/tenant/dashboard" });
      }
    },
    [navigate],
  );

  if (loading) {
    return (
      <ConsentShell>
        <div
          className="flex items-center justify-center gap-3 py-16 text-tertiary"
          aria-busy="true"
          aria-live="polite"
        >
          <LoadingIndicator /> <span>Načítám…</span>
        </div>
      </ConsentShell>
    );
  }

  if (error) {
    return (
      <ConsentShell>
        <div className="space-y-5 rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
          <InlineAlert variant="error" title="Nepodařilo se načíst podmínky">
            {error}
          </InlineAlert>
          <div className="flex flex-wrap gap-3">
            <Button color="primary" onPress={() => setReloadKey((k) => k + 1)}>
              Zkusit znovu
            </Button>
            <Button color="secondary" onPress={() => void handleLogout()}>
              Odhlásit se
            </Button>
          </div>
        </div>
      </ConsentShell>
    );
  }

  if (!version) {
    return (
      <ConsentShell>
        <div className="space-y-5 rounded-xl bg-primary p-6 text-center shadow-xs ring-1 ring-secondary">
          <h1 className="m-0 text-xl font-semibold text-primary">Nic k odsouhlasení</h1>
          <p className="m-0 text-md text-tertiary">
            Pro váš účet teď není žádná aktualizace podmínek k potvrzení.
          </p>
          <Button color="primary" onPress={() => void navigate({ to: "/" })}>
            Pokračovat
          </Button>
        </div>
      </ConsentShell>
    );
  }

  return (
    <ConsentShell>
      <div className="space-y-6">
        <header className="flex flex-col items-center gap-2 text-center">
          <h1 className="m-0 text-display-xs font-semibold text-primary">Aktualizace podmínek</h1>
          <p className="m-0 text-md text-tertiary">
            Podmínky zpracování byly aktualizovány — přečtěte si změny a znovu
            prosím udělte souhlas.
          </p>
        </header>

        <ConsentStepper
          consentTextVersion={version}
          consentTextHtml={text}
          previousVersion={previousVersion ?? undefined}
          previousVersionHtml={previousText || undefined}
          onAccept={handleAccept}
          onCancel={() => void handleLogout()}
        />

        {previousVersion && (
          <aside
            className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary"
            aria-label="Co se změnilo"
          >
            <h2 className="mt-0 mb-3 text-md font-semibold text-primary">Co se změnilo</h2>
            <DiffViewer previous={stripHtml(previousText)} current={stripHtml(text)} />
          </aside>
        )}
      </div>
    </ConsentShell>
  );
}

function stripHtml(html: string): string {
  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent ?? "";
  }
  return html.replace(/<[^>]*>/g, "");
}

function buildLegacyFallback(version: string): string {
  return `
    <div role="alert" style="background:#fff3cd;border:1px solid #ffe58f;padding:0.75rem;border-radius:4px;margin-bottom:1rem">
      <strong>Text souhlasu zatím není k dispozici.</strong>
      Tato verze byla vytvořena dříve, než přibyl povinný text souhlasu.
      Kontaktuj administrátora — verzi je potřeba znovu publikovat s textací.
    </div>
    <h2>Souhlas se zpracováním osobních údajů</h2>
    <p>Verze ${version}.</p>
  `.trim();
}

function buildPreviousVersionPlaceholder(version: string): string {
  // Tenant endpoint pro libovolnou verzi text body chybí (follow-up úkol).
  // Pro diff view tedy nemáme reálný text předchozí verze — vrátíme jen popis.
  return `Verze ${version} — předchozí znění souhlasu není k dispozici.`;
}
